import { useCallbackExperiment } from "@million/lint/runtime";

import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { Button } from "@faire/web--source/slate/Button";
import Checkbox from "@faire/web--source/slate/Checkbox";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { Row } from "@faire/web--source/slate/Layout";
import Radio from "@faire/web--source/slate/Radio";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Span, Typography } from "@faire/web--source/slate/Typography";
import { IFilterOption } from "@faire/web-api--source/indigofair/data/IFilterOption";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import React, { useState, type JSX } from "react";
import styled from "styled-components";

import {
  getFilteredOptions,
  hasSelectedOptions,
  recursivelyClearFilter,
  recursivelyToggleFilter,
  selectIfSelectedAllSubOptions,
  updateAllSelectableSubOptions,
} from "@faire/retailer/components/Filters/FilterUtils";
import { useFilterSectionSelectedChange } from "@faire/retailer/components/Filters/useFilterSectionSelectedChange";
import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { getSettingAndAssignMillionLintOptimization } from "@faire/retailer/settings/getSettingAndAssignMillionLintOptimization";

interface IProps {
  isSubOption: boolean;
  section: IFilterSection;
  option: IFilterOption;
  searchValue: string;
  onChange: (
    section: IFilterSection,
    filterKey: string,
    value: boolean
  ) => void;
  renderOption: (
    option: IFilterOption,
    isSubOption: boolean
  ) => JSX.Element | null;
  className?: string;
  onExpand?: (options: IFilterOption) => void;
  showUnformattedResultsCount?: boolean;
}

export const FilterOption: React.FC<IProps> = ({
  isSubOption,
  option,
  section,
  searchValue,
  renderOption,
  onChange,
  onExpand,
  showUnformattedResultsCount = false,
}) => {
  const shouldMemoMillionLint = getSettingAndAssignMillionLintOptimization();

  /** Million Experiment: This function was originally passed as an unstable prop to FilterOption.
   * However, it's hard to convert it to an experiment so we moved the function here and wrapped it with useCallbackExperiment.
   * When the experiment is not running, the performance remains the same because both renderSubOptions and renderOption are unstable (view PR for definition)
   * Since both are unstable, both will cause an re-render to occur due to prop changes.
   * In terms of functionality, we are just moving the function from the parent to the child.
   */
  const renderSubOptions = useCallbackExperiment(
    // eslint-disable-next-line @faire/no-inline-hoc-fc
    (options: IFilterOption[]) => {
      if (options.length === 0) {
        return null;
      }

      return (
        <SuboptionsRow>
          <Spacer size="2x" />
          <OptionsWrapper>
            {options.map((option) => renderOption(option, true))}
          </OptionsWrapper>
        </SuboptionsRow>
      );
    },
    [renderOption],
    shouldMemoMillionLint
  );

  /** View comment at line 151 */
  const selected = shouldMemoMillionLint
    ? // eslint-disable-next-line react-compiler/react-compiler
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useFilterSectionSelectedChange(() => !!option.is_selected)
    : !!option.is_selected;

  const disabled = option.results_count === 0 && !selected;
  const intermediate =
    section.selection_type === IFilterSection.SelectionType.MULTIPLE &&
    !selected &&
    hasSelectedOptions(option);
  const singleSelection =
    section.selection_type === IFilterSection.SelectionType.SINGLE ||
    section.selection_type === IFilterSection.SelectionType.SINGLE_REQUIRED;
  const filteredSubOptions = React.useMemo(
    () => getFilteredOptions(option.sub_options, searchValue),
    [option.sub_options, searchValue]
  );

  const { strictLocalize } = useStrictLocalization();
  const {
    data: { hideOptionsResultCount },
  } = useFilterStore(["hideOptionsResultCount"]);

  const [collapsed, setCollapsed] = useState(!intermediate);

  const showSubOptions = !collapsed || searchValue !== "";

  const handleHeaderClick = useCallbackExperiment(
    () => {
      if (collapsed) {
        onExpand?.(option);
      }
      setCollapsed(!collapsed);
    },
    [collapsed, onExpand, option],
    shouldMemoMillionLint
  );

  const toggleFilterOption = useCallbackExperiment(
    () => {
      if (disabled) {
        return;
      }

      if (singleSelection) {
        section.options?.forEach((thisOption) => {
          recursivelyClearFilter(thisOption, option.key);
        });
      }

      const isRequired =
        section.selection_type === IFilterSection.SelectionType.SINGLE_REQUIRED;
      if (isRequired) {
        section.options?.forEach((thisOption) => {
          recursivelyToggleFilter(thisOption, option.key ?? "", true);
        });
      } else {
        const toggle = !option.is_selected;
        section.options?.forEach((thisOption) => {
          recursivelyToggleFilter(thisOption, option.key ?? "", toggle);
        });
      }

      if (!singleSelection) {
        updateAllSelectableSubOptions(option);
        section.options.forEach(selectIfSelectedAllSubOptions);
      }

      onChange(section, option.key ?? "", option.is_selected ?? false);
      /**
       * Previously, there would be 3 mass re-renders
       * 1. When the FilterOptions was clicked (caused by "isNewFilterSelected" changing from false to true)
       * 2. When the updated filterSections were fetched (caused by "filterSections" changing)
       * 3. "isNewFilterSelected" changing from true to false.
       * Note: some of these re-renders might be batched sometimes
       *
       * Each of these re-renders were causing the FilterOption's Radio / Checkbox "checked" field to re-render causing it to change because "filterSections" has a new reference each time despite being the same value..
       * However, now that we are only updating "filterSections" when the value changes, we don't have a way to trigger a re-render of the FilterOption's Radio / Checkbox "checked" field since the reference doesn't change.
       * So instead, we update the version, causing only the specific FilterOptions that actually changed to re-render instead of all the FilterOptions.
       *
       * We are using a Zustand store here so we can have a selector on top of FilterOption's state to trigger a re-render of the specific FilterOption ONLY if it's selected field has changed.
       * This is hacky and the final optimization is to move the custom useFilterSections store as it's own Zustand store.
       */
      useFilterSectionSelectedChange.getState().optionChanged();
    },
    [disabled, onChange, option, section, singleSelection],
    shouldMemoMillionLint
  );

  //TODO(Juliette.li@faire): either ask BE to add formatted_results_count or create one for logged-out category pages.
  const renderLabel = () => {
    return (
      <FilterOptionText id={`filter-${option.key}`}>
        <FilterOptionTextSpan
          $disabled={disabled}
          data-show-result-count={
            !!(option.formatted_results_count && !hideOptionsResultCount)
          }
          color={disabled ? Core.text.subdued : Core.text.default}
          aria-label={option.display_name}
          title={option.display_name}
        >
          {option.display_name}
        </FilterOptionTextSpan>
        {option.formatted_results_count && !hideOptionsResultCount ? (
          <ResultCountText color={Core.text.subdued}>
            {showUnformattedResultsCount && !!option.results_count
              ? option.results_count
              : option.formatted_results_count}
          </ResultCountText>
        ) : null}
      </FilterOptionText>
    );
  };

  const renderSelectionType = () => {
    const isNotEcoFriendlyOption = !(
      !isSubOption &&
      section.field_name ===
        IFilterSection.FieldName.ECO_FRIENDLY_PRODUCT_ATTRIBUTES
    );

    if (singleSelection) {
      return (
        <Radio
          name={option.key}
          active={selected}
          disabled={disabled}
          onClick={toggleFilterOption}
          align="flex-start"
        >
          {renderLabel()}
        </Radio>
      );
    } else if (isNotEcoFriendlyOption) {
      return (
        <Checkbox
          checked={selected}
          intermediate={intermediate}
          disabled={disabled}
          onChange={toggleFilterOption}
          align="flex-start"
        >
          <CBFixWrapper>{renderLabel()}</CBFixWrapper>
        </Checkbox>
      );
    }

    return <CBFixWrapper>{renderLabel()}</CBFixWrapper>;
  };

  if (!option.key) {
    return null;
  }

  return (
    <React.Fragment>
      <OptionRow
        $disabled={disabled}
        $selected={!!option.is_selected || intermediate}
        $twoColumnLayout={false}
      >
        {renderSelectionType()}
        {!disabled && filteredSubOptions.length > 0 ? (
          <ChevronWrapper
            onClick={handleHeaderClick}
            aria-label={
              collapsed
                ? strictLocalize(
                    {
                      defaultMessage: "Expand {filterNameToken} filter",
                      description: {
                        text: "Aria label for expand filter option",
                        img: "https://cdn.faire.com/fastly/d9ff7f62068099d2ea7051eea3a53afca185cf9720d6ff116b0ab2abe352dcbe.png",
                      },
                    },
                    {
                      filterNameToken: option.display_name ?? option.key,
                    }
                  )
                : strictLocalize(
                    {
                      defaultMessage: "Collapse {filterNameToken} filter",
                      description: {
                        text: "Aria label for collapse filter option",
                        img: "https://cdn.faire.com/fastly/d9ff7f62068099d2ea7051eea3a53afca185cf9720d6ff116b0ab2abe352dcbe.png",
                      },
                    },
                    {
                      filterNameToken: option.display_name ?? option.key,
                    }
                  )
            }
          >
            <StyledChevron
              direction={showSubOptions ? "up" : "down"}
              color={Core.icon.default}
              width="12px"
            />
          </ChevronWrapper>
        ) : null}
      </OptionRow>
      {showSubOptions ? renderSubOptions(filteredSubOptions) : null}
    </React.Fragment>
  );
};

const ChevronWrapper = styled(Button).attrs({
  variant: "tertiary",
})`
  flex-grow: 1;
  justify-content: flex-end;
`;

const StyledChevron = styled(Chevron)`
  float: right;
  margin-top: 2px;
`;

const FilterOptionTextSpan = styled(Span)<{
  $disabled: boolean;
}>`
  word-break: break-word;
  -ms-word-break: break-word;
  cursor: pointer;
  &[title]:hover {
    cursor: pointer;
  }
  &:hover {
    color: ${(props) =>
      props.$disabled ? Core.text.subdued : Core.text.hover};
    cursor: pointer;
  }

  &[data-show-result-count="false"] {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const FilterOptionText = styled(Typography)`
  float: left;
`;

const ResultCountText = styled(Span)`
  &&& {
    margin-left: ${getSpacing("1x")};
  }
`;

const OptionOrSeeMoreRow = styled(Row).attrs({ align: "flex-start" })`
  position: relative;
  width: 100%;
  padding-bottom: ${getSpacing("1x")};
`;

const OptionRow = styled(OptionOrSeeMoreRow)<{
  $disabled: boolean;
  $selected: boolean;
  $twoColumnLayout?: boolean;
}>`
  width: ${({ $twoColumnLayout: twoColumnLayout }) =>
    twoColumnLayout ? "50%" : "100%"};
  cursor: ${({ $disabled: disabled }) => (disabled ? "default" : "pointer")};

  .radio {
    border-color: ${({ $disabled: disabled }) =>
      disabled ? Core.border.subdued : Core.border.default};
  }

  &:hover {
    .radio {
      border-color: ${({ $disabled: disabled }) =>
        disabled ? Core.border.subdued : Core.border.hover};
    }

    ${FilterOptionText} {
      color: ${({ $disabled: disabled }) =>
        disabled ? Core.text.subdued : Core.text.enabled};
    }
  }
`;

const CBFixWrapper = styled.div`
  margin-top: -2px;
`;

const SuboptionsRow = styled(Row)`
  width: 100%;
`;

const OptionsWrapper = styled(Row).attrs({ align: "center" })`
  position: relative;
  width: 100%;
  flex-wrap: wrap;
`;
